// Floating Rate Bond Pricing Page
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CurveSetSelector from '../../components/products/CurveSetSelector';
import PricingResults from '../../components/products/PricingResults';
import IndexPicker from '../../components/curves/IndexPicker';
import { Curve, CurvePoint, IndexRef, IndexDef, DAY_COUNTERS, CALENDARS, collectIndexRefIds } from '../../lib/types';
import { quantraApi, type PricingErrorInfo } from '../../lib/quantra-api';
import { normalizeCurveForApi, normalizeIndexDefForApi } from '../../lib/api-normalizers';
import { floatingBondStore, SavedFloatingRateBond, generateId } from '../../lib/storage/bonds';
import { indexStore, storedToRateIndexDef } from '../../lib/storage/indices';
import { useAsOfDate } from '../../hooks/useAsOfDate';
import { getQuoteBook } from '../../lib/storage/quoteBook';
import { buildPricingQuoteSnapshotWithBackend } from '../../lib/marketDataBackend';
import { useAuth } from '../../hooks/useAuth';
import { resolveCurveArrayQuoteIds } from '../../lib/curve-quote-resolution';
import BackLink from '../../components/ui/BackLink';
import FeedbackBanner from '../../components/ui/FeedbackBanner';
import PageHeader from '../../components/ui/PageHeader';
import ProductSaveBar from '../../components/products/ProductSaveBar';
import { entityUi, getBackLabel } from '../../components/ui/entityUi';
import { formStyles } from '../../components/ui/formStyles';

interface FloatingBondParams {
  settlementDays: number;
  faceAmount: number;
  spread: number;
  accrualDayCounter: string;
  paymentConvention: string;
  fixingDays: number;
  inArrears: boolean;
  redemption: number;
  issueDate: string;
  effectiveDate: string;
  terminationDate: string;
  frequency: string;
  calendar: string;
  convention: string;
  dateGenerationRule: string;
}

const FREQUENCIES = ['Annual', 'Semiannual', 'Quarterly', 'Monthly'];
const DATE_GEN_RULES = ['Forward', 'Backward'];
const DEFAULT_YIELD = {
  day_counter: 'Actual360',
  compounding: 'Compounded',
  frequency: 'Annual',
};

/** Resolve IndexDef objects for a list of index IDs (saved → skip) */
async function resolveIndexDefs(ids: string[]): Promise<IndexDef[]> {
  if (ids.length === 0) return [];
  const savedSpecs = await indexStore.getAll();
  const result: IndexDef[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    const saved = savedSpecs.find(s => s.id === id);
    if (!saved) continue;
    const def = storedToRateIndexDef(saved);
    if (def) result.push(def);
  }
  return result;
}

function collectIndexIdsFromCurves(curves: Array<{ points?: CurvePoint[] }>): string[] {
  const allPoints = curves.flatMap(c => c.points || []);
  return collectIndexRefIds(allPoints);
}

export default function FloatingRateBondPricer() {
  const ui = entityUi.floatingRateBond;
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  
  // Dates
  const today = new Date().toISOString().split('T')[0];
  const fiveYearsLater = new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  const [asOfDate, setAsOfDate] = useState(today);
  const [attachQuotes, setAttachQuotes] = useState(true);
  const { asOfDate: globalAsOf } = useAsOfDate();
  useEffect(() => { setAsOfDate(globalAsOf); }, [globalAsOf]);
  const [settlementDate, setSettlementDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().split('T')[0];
  });
  
  // Bond name for save
  const [bondName, setBondName] = useState('');
  const [bondId, setBondId] = useState(id && id !== 'new' ? id : '');
  const [saveStatus, setSaveStatus] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Curve selection
  const [curveSetId, setCurveSetId] = useState('');
  const [discountCurveId, setDiscountCurveId] = useState('');
  const [discountCurve, setDiscountCurve] = useState<Curve | null>(null);
  const [forecastCurveId, setForecastCurveId] = useState('');
  const [forecastCurve, setForecastCurve] = useState<Curve | null>(null);
  const [useSameCurve, setUseSameCurve] = useState(true);
  
  // Bond parameters
  const [bond, setBond] = useState<FloatingBondParams>({
    settlementDays: 2,
    faceAmount: 100,
    spread: 0.001, // 10 bps
    accrualDayCounter: 'Actual360',
    paymentConvention: 'ModifiedFollowing',
    fixingDays: 2,
    inArrears: false,
    redemption: 100,
    issueDate: today,
    effectiveDate: today,
    terminationDate: fiveYearsLater,
    frequency: 'Semiannual',
    calendar: 'TARGET',
    convention: 'ModifiedFollowing',
    dateGenerationRule: 'Forward',
  });
  
  // Index reference (points to an IndexDef id)
  const [indexRef, setIndexRef] = useState<IndexRef>({ id: 'EURIBOR_6M' });
  
  // Pricing options
  const [includeFlows, setIncludeFlows] = useState(true);
  const [includeDetails, setIncludeDetails] = useState(true);
  
  // Results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorInfo, setErrorInfo] = useState<PricingErrorInfo | undefined>(undefined);
  const [result, setResult] = useState<any>(null);
  const [durationMs, setDurationMs] = useState<number | undefined>();
  const [lastRequest, setLastRequest] = useState<object | null>(null);
  const { user, login } = useAuth();
  
  // Load existing bond
  useEffect(() => {
    if (!isNew && id) {
      floatingBondStore.getById(id).then(saved => {
        if (saved) {
          setBondName(saved.name);
          setBondId(saved.id);
          setBond({
            settlementDays: saved.settlementDays,
            faceAmount: saved.faceAmount,
            spread: saved.spread,
            accrualDayCounter: saved.accrualDayCounter,
            paymentConvention: saved.paymentConvention,
            fixingDays: saved.fixingDays,
            inArrears: saved.inArrears,
            redemption: saved.redemption,
            issueDate: saved.issueDate,
            effectiveDate: saved.effectiveDate,
            terminationDate: saved.terminationDate,
            frequency: saved.frequency,
            calendar: saved.calendar,
            convention: saved.convention,
            dateGenerationRule: saved.dateGenerationRule,
          });
          // Load index ref — use saved indexRefId if available, else fallback to EURIBOR_6M
          if ((saved as any).indexRefId) {
            setIndexRef({ id: (saved as any).indexRefId });
          }
          setDiscountCurveId(saved.discountCurveId);
          setForecastCurveId(saved.forecastCurveId);
          setUseSameCurve(saved.useSameCurve);
        }
      });
    }
  }, [id, isNew]);
  
  const handleSaveBond = async () => {
    setSaveStatus(null);
    setSaving(true);
    try {
      const saveId = bondId || generateId();
      const finalName = bondName.trim() || `Floating Bond +${bond.spread * 10000}bp ${bond.terminationDate}`;
      const saved: SavedFloatingRateBond = {
        id: saveId,
        name: finalName,
        settlementDays: bond.settlementDays,
        faceAmount: bond.faceAmount,
        spread: bond.spread,
        accrualDayCounter: bond.accrualDayCounter,
        paymentConvention: bond.paymentConvention,
        fixingDays: bond.fixingDays,
        inArrears: bond.inArrears,
        redemption: bond.redemption,
        issueDate: bond.issueDate,
        effectiveDate: bond.effectiveDate,
        terminationDate: bond.terminationDate,
        frequency: bond.frequency,
        calendar: bond.calendar,
        convention: bond.convention,
        dateGenerationRule: bond.dateGenerationRule,
        indexPeriodNumber: 0,
        indexPeriodTimeUnit: '',
        indexSettlementDays: 0,
        indexCalendar: '',
        indexBusinessDayConvention: '',
        indexEndOfMonth: false,
        indexDayCounter: '',
        indexRefId: indexRef.id,
        discountCurveId: discountCurveId,
        forecastCurveId: forecastCurveId,
        useSameCurve,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await floatingBondStore.save(saved);
      setBondId(saveId);
      setSaveStatus({ tone: 'success', message: `Saved "${finalName}".` });
      if (isNew) navigate(`/products/floating-rate-bond/${saveId}`, { replace: true });
    } catch (err) {
      setSaveStatus({ tone: 'error', message: err instanceof Error ? err.message : 'Failed to save bond' });
    } finally {
      setSaving(false);
    }
  };
  
  const updateBond = (field: keyof FloatingBondParams, value: any) => {
    setBond(prev => ({ ...prev, [field]: value }));
  };
  
  const handleDownloadRequest = () => {
    if (!lastRequest) return;
    const blob = new Blob([JSON.stringify(lastRequest, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `floating-rate-bond-request-${asOfDate}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  const handlePrice = async () => {
    if (!user) {
      const signedInUser = await login();
      if (!signedInUser) {
        setError('Sign in is required to run pricing calls.');
        return;
      }
    }
    if (!discountCurve) {
      setError('Please select a discount curve');
      return;
    }
    
    const actualForecastCurve = useSameCurve ? discountCurve : forecastCurve;
    if (!actualForecastCurve) {
      setError('Please select a forecast curve');
      return;
    }
    
    setLoading(true);
    setError(null);
    setErrorInfo(undefined);
    setResult(null);
    
    try {
      // Build curves for API
      const curves = [normalizeCurveForApi({
        id: 'discount',
        day_counter: discountCurve.day_counter,
        interpolator: discountCurve.interpolator,
        bootstrap_trait: discountCurve.bootstrap_trait,
        reference_date: discountCurve.reference_date || asOfDate,
        points: discountCurve.points,
      })];
      
      // Add forecast curve if different
      if (!useSameCurve && forecastCurve && forecastCurve.id !== discountCurve.id) {
        curves.push(normalizeCurveForApi({
          id: 'forecast',
          day_counter: forecastCurve.day_counter,
          interpolator: forecastCurve.interpolator,
          bootstrap_trait: forecastCurve.bootstrap_trait,
          reference_date: forecastCurve.reference_date || asOfDate,
          points: forecastCurve.points,
        }));
      }
      
      const quoteBookEntries = getQuoteBook();
      const quoteFixingsById = new Map(
        quoteBookEntries.map((entry) => [
          entry.id,
          (entry.series || []).filter((s) => s.date <= asOfDate).map((s) => ({ date: s.date, value: s.value })),
        ])
      );
      const indexFixingsById = new Map(
        (await indexStore.getAll())
          .filter(i => Array.isArray(i.fixings) && i.fixings.length > 0)
          .map(i => [
            i.id,
            i.fixings!.filter(f => f.date <= asOfDate).map(f => ({ date: f.date, value: f.value })),
          ])
      );
      const fixingsById = new Map<string, { date: string; value: number }[]>();
      for (const [id, series] of quoteFixingsById) fixingsById.set(String(id), series);
      for (const [id, series] of indexFixingsById) {
        if (series.length > 0) fixingsById.set(id, series);
      }

      const indexFixings = fixingsById.get(indexRef.id) || [];
      if (asOfDate > bond.effectiveDate && indexFixings.length === 0) {
        setError(`Missing ${indexRef.id} fixings for a seasoned bond. Add fixings in Market Data → Indices or Quote Book (id: ${indexRef.id}) or price with an as-of date on/before the effective date.`);
        setLoading(false);
        return;
      }

      // Resolve IndexDef for referenced index + any curve helper indices
      const curveIndexIds = collectIndexIdsFromCurves(curves);
      const allIndexIds = Array.from(new Set([indexRef.id, ...curveIndexIds]));
      const resolvedIndices = await resolveIndexDefs(allIndexIds);
      const resolvedIndicesWithFixings = resolvedIndices.map(def => {
        const fixings = fixingsById.get(def.id) || [];
        return fixings.length > 0 ? { ...def, fixings } : def;
      });
      const unresolvedIds = allIndexIds.filter(id => !resolvedIndices.find(d => d.id === id));
      if (unresolvedIds.length > 0) {
        setError(`Selected curve references unknown indices: ${unresolvedIds.join(', ')}. Add them in Market Data → Indices.`);
        setLoading(false);
        return;
      }

      const pricingQuotes = attachQuotes
        ? (await buildPricingQuoteSnapshotWithBackend(asOfDate, { quoteType: 'Curve' })).quotes
        : [];

      const resolvedCurvesForApi = resolveCurveArrayQuoteIds(curves, asOfDate).map(normalizeCurveForApi);

      const request = {
        pricing: {
          as_of_date: asOfDate,
          settlement_date: settlementDate,
          indices: resolvedIndicesWithFixings.map(normalizeIndexDefForApi),
          curves: resolvedCurvesForApi,
          ...(attachQuotes ? { quotes: pricingQuotes } : {}),
          bond_pricing_flows: includeFlows,
          bond_pricing_details: includeDetails,
          coupon_pricers: [{
            id: 'iborpricer',
            black_ibor_coupon_pricer: {
              optionlet_volatility: {
                settlement_days: 2,
                calendar: bond.calendar,
                business_day_convention: bond.paymentConvention,
                volatility: 0,
                day_counter: 'Actual365Fixed',
              },
            },
          }],
        },
        bonds: [{
          floating_rate_bond: {
            settlement_days: bond.settlementDays,
            face_amount: bond.faceAmount,
            schedule: {
              calendar: bond.calendar,
              effective_date: bond.effectiveDate,
              termination_date: bond.terminationDate,
              frequency: bond.frequency,
              convention: bond.convention,
              termination_date_convention: bond.convention,
              date_generation_rule: bond.dateGenerationRule,
            },
            index: { id: indexRef.id },
            accrual_day_counter: bond.accrualDayCounter,
            payment_convention: bond.paymentConvention,
            fixing_days: bond.fixingDays,
            spread: bond.spread,
            in_arrears: bond.inArrears,
            redemption: bond.redemption,
            issue_date: bond.issueDate,
          },
          discounting_curve: 'discount',
          forwarding_curve: useSameCurve ? 'discount' : 'forecast',
          coupon_pricer: 'iborpricer',
          yield: DEFAULT_YIELD,
        }],
      };
      
      const response = await quantraApi.priceFloatingRateBond(request);
      
      setDurationMs(response.duration_ms);
      
      if (response.success && response.data?.bonds?.[0]) {
        setResult(response.data.bonds[0]);
        setLastRequest(request);
      } else {
        setError(response.error || 'Pricing failed');
        setErrorInfo(response.errorInfo);
        setLastRequest(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pricing failed');
      setErrorInfo(undefined);
      setLastRequest(null);
    } finally {
      setLoading(false);
    }
  };
  
  const inputClass = formStyles.input;
  const labelClass = formStyles.label;
  
  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
        {/* Header */}
        <PageHeader
          title={isNew ? 'New Floating Rate Bond' : (bondName.trim() || 'Floating Rate Bond')}
          subtitle="Price floating rate notes linked to IBOR indices"
          backLink={<BackLink onClick={() => navigate('/products/floating-rate-bond')} label={getBackLabel(ui.plural)} />}
          actions={
            <ProductSaveBar
              name={bondName}
              onNameChange={setBondName}
              onSave={handleSaveBond}
              saving={saving}
              placeholder="Bond name…"
            />
          }
        />
        {saveStatus && (
          <FeedbackBanner
            tone={saveStatus.tone}
            message={saveStatus.message}
            onDismiss={() => setSaveStatus(null)}
          />
        )}
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Bond Parameters */}
          <div className="lg:col-span-2 space-y-6">
            {/* Pricing Context */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-5">
              <h2 className="text-sm font-semibold text-[#0a0a0a] mb-4">Pricing Context</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>As Of Date</label>
                  <input
                    type="date"
                    value={asOfDate}
                    onChange={e => setAsOfDate(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Settlement Date</label>
                  <input
                    type="date"
                    value={settlementDate}
                    onChange={e => setSettlementDate(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <CurveSetSelector
                  label="Discounting Curve (PV)"
                  curveRole="discount"
                  curveSetId={curveSetId}
                  curveId={discountCurveId}
                  onChangeCurveSet={(csId) => setCurveSetId(csId)}
                  onChangeCurve={(id, curve) => {
                    setDiscountCurveId(id);
                    setDiscountCurve(curve);
                  }}
                />
                <div>
                  <label className="flex items-center gap-2 text-xs text-[#737373] mb-1.5 font-medium">
                    Forecasting Curve (Index projection)
                    <label className="flex items-center gap-1 text-[#525252] font-normal">
                      <input
                        type="checkbox"
                        checked={useSameCurve}
                        onChange={e => setUseSameCurve(e.target.checked)}
                        className="rounded border-[#d4d4d4] w-3 h-3"
                      />
                      <span className="text-xs">Single-curve mode</span>
                    </label>
                  </label>
                  {useSameCurve ? (
                    <input
                      type="text"
                      value={discountCurve?.name || 'Select discount curve first'}
                      disabled
                      className={`${inputClass} bg-[#f5f5f5] text-[#737373]`}
                    />
                  ) : (
                    <CurveSetSelector
                      label=""
                      curveRole="forward"
                      curveSetId={curveSetId}
                      curveId={forecastCurveId}
                      onChangeCurveSet={(csId) => setCurveSetId(csId)}
                      onChangeCurve={(id, curve) => {
                        setForecastCurveId(id);
                        setForecastCurve(curve);
                      }}
                    />
                  )}
                </div>
              </div>
              {/* Attach Quote Snapshot Toggle */}
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="checkbox"
                  id="attachQuotesFloat"
                  checked={attachQuotes}
                  onChange={e => setAttachQuotes(e.target.checked)}
                  className="rounded border-[#d4d4d4] text-[#8a6a2f] focus:ring-[#8a6a2f]"
                />
                <label htmlFor="attachQuotesFloat" className="text-xs text-[#737373]">
                  Attach quote book snapshot to pricing request
                </label>
              </div>
            </div>
            
            {/* Bond Details */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-5">
              <h2 className="text-sm font-semibold text-[#0a0a0a] mb-4">Bond Details</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Face Amount</label>
                  <input
                    type="number"
                    value={bond.faceAmount}
                    onChange={e => updateBond('faceAmount', parseFloat(e.target.value) || 100)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Spread (bps)</label>
                  <input
                    type="number"
                    step="1"
                    value={(bond.spread * 10000).toFixed(0)}
                    onChange={e => updateBond('spread', (parseFloat(e.target.value) || 0) / 10000)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Redemption</label>
                  <input
                    type="number"
                    value={bond.redemption}
                    onChange={e => updateBond('redemption', parseFloat(e.target.value) || 100)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Settlement Days</label>
                  <input
                    type="number"
                    value={bond.settlementDays}
                    onChange={e => updateBond('settlementDays', parseInt(e.target.value) || 2)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Fixing Days</label>
                  <input
                    type="number"
                    value={bond.fixingDays}
                    onChange={e => updateBond('fixingDays', parseInt(e.target.value) || 2)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Accrual Day Counter</label>
                  <select
                    value={bond.accrualDayCounter}
                    onChange={e => updateBond('accrualDayCounter', e.target.value)}
                    className={inputClass}
                  >
                    {DAY_COUNTERS.map(dc => <option key={dc} value={dc}>{dc}</option>)}
                  </select>
                </div>
              </div>
              
              <div className="mt-4">
                <label className="flex items-center gap-2 text-sm text-[#525252]">
                  <input
                    type="checkbox"
                    checked={bond.inArrears}
                    onChange={e => updateBond('inArrears', e.target.checked)}
                    className="rounded border-[#d4d4d4]"
                  />
                  In Arrears
                </label>
              </div>
            </div>
            
            {/* Index */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-5">
              <h2 className="text-sm font-semibold text-[#0a0a0a] mb-4">Floating Index</h2>
              <IndexPicker
                label="IBOR Index"
                value={indexRef}
                onChange={(ref: IndexRef, _def: IndexDef) => setIndexRef(ref)}
                filter="Ibor"
              />
            </div>
            
            {/* Schedule */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-5">
              <h2 className="text-sm font-semibold text-[#0a0a0a] mb-4">Schedule</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Issue Date</label>
                  <input
                    type="date"
                    value={bond.issueDate}
                    onChange={e => updateBond('issueDate', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Effective Date</label>
                  <input
                    type="date"
                    value={bond.effectiveDate}
                    onChange={e => updateBond('effectiveDate', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Maturity Date</label>
                  <input
                    type="date"
                    value={bond.terminationDate}
                    onChange={e => updateBond('terminationDate', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Frequency</label>
                  <select
                    value={bond.frequency}
                    onChange={e => updateBond('frequency', e.target.value)}
                    className={inputClass}
                  >
                    {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Calendar</label>
                  <select
                    value={bond.calendar}
                    onChange={e => updateBond('calendar', e.target.value)}
                    className={inputClass}
                  >
                    {CALENDARS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Date Generation</label>
                  <select
                    value={bond.dateGenerationRule}
                    onChange={e => updateBond('dateGenerationRule', e.target.value)}
                    className={inputClass}
                  >
                    {DATE_GEN_RULES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Options */}
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-[#525252]">
                <input
                  type="checkbox"
                  checked={includeDetails}
                  onChange={e => setIncludeDetails(e.target.checked)}
                  className="rounded border-[#d4d4d4]"
                />
                Include risk metrics
              </label>
              <label className="flex items-center gap-2 text-sm text-[#525252]">
                <input
                  type="checkbox"
                  checked={includeFlows}
                  onChange={e => setIncludeFlows(e.target.checked)}
                  className="rounded border-[#d4d4d4]"
                />
                Include cash flows
              </label>
            </div>
            
            {/* Price Button */}
            <div className="flex gap-2">
              <button
                onClick={handlePrice}
                disabled={loading || !discountCurve}
                className="flex-1 py-3 text-sm font-medium text-white bg-[#0a0a0a] rounded-xl hover:bg-[#262626] disabled:opacity-50 transition-colors"
              >
                {loading ? 'Pricing...' : 'Price Bond'}
              </button>
              {lastRequest && (
                <button
                  onClick={handleDownloadRequest}
                  className="px-4 py-3 text-sm font-medium text-[#8a6a2f] bg-[#f5f0e6] border border-[#e5d9c3] rounded-xl hover:bg-[#efe5d3] transition-colors flex items-center gap-1.5"
                  title="Download API request JSON"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  Request JSON
                </button>
              )}
            </div>
          </div>
          
          {/* Right: Results */}
          <div>
            <PricingResults
              loading={loading}
              error={error}
              errorInfo={errorInfo}
              result={result}
              durationMs={durationMs}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
